import _, {chain, groupBy, mapValues} from 'lodash';
import {createSelector} from 'reselect';

import {ErrorWithData} from '../../../../libs/shared/helpers/src/types/customErrors';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {getContactSourceFromMessageType} from '../../../helpers/modelHelpers';
import {selectCardViaPriorityOrder} from '../../../helpers/models/cardHelpers';
import {findCardForContactInNamespace} from '../../../helpers/models/contactHelpers';
import {
  findRecipientForVariableInterpolation,
  VisualRecipientMap,
} from '../../../helpers/models/recipientHelpers';
import {AppParametricSelector, AppSelector, wrapInShallowSelector} from '../../../helpers/reselectHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {buildContactForHandle, ContactModel, parseContactUid} from '../../../models/api/contactModel';
import {ConversationBaseModel} from '../../../models/api/conversationModel';
import {MessageModel} from '../../../models/api/messageModel';
import {RecipientModel} from '../../../models/api/recipientModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {AppState} from '../../../store/appState';
import {ResourceMap} from '../../appTypes';
import {getChannels} from '../channels/channelsSelectors';
import {getCards, getContacts} from '../dataSelectors';

/*
 * Single visual recipient.
 */

/** Find the conversation's recipient. */
export function makeGetRecipient<P>(
  getConversation: AppParametricSelector<P, ConversationBaseModel | undefined>,
  getTeammateId: AppParametricSelector<P, number>,
) {
  return createSelector([getConversation, getTeammateId], (conversation, teammateId) => {
    if (!conversation) {
      return undefined;
    }

    // Check if there's a recipient in our teammate's tracker.
    const {trackers, recipient} = conversation;
    const teammateTracker = trackers[teammateId];
    const teammateRecipient = teammateTracker && teammateTracker.recipient;

    // If not, default to the conversation's recipient.
    return teammateRecipient || recipient;
  });
}

/** Find the contact for the conversation's recipient. */
export function makeGetContact<P>(
  getRecipient: AppParametricSelector<P, RecipientModel | undefined>,
): AppParametricSelector<P, ContactModel | undefined> {
  return createSelector([getContacts, getRecipient], (contacts, recipient) => {
    if (!recipient) {
      return undefined;
    }

    // Find the corresponding contact.
    const {contactUid} = recipient;
    const contact = contacts[contactUid];
    return contact;
  });
}

/** Find the fallback contact for the conversation's recipient. */
export function makeGetFallbackContact<P>(getContact: AppParametricSelector<P, ContactModel | undefined>) {
  return createSelector([getContact, getContacts], (contact, contacts) => {
    const fallbackContactUid = contact && contact.fallbackContactUid;
    return (fallbackContactUid && contacts[fallbackContactUid]) || undefined;
  });
}

/** Find the card, if any, for the conversation's contact. */
export function makeGetCard<P>(
  getConversation: AppParametricSelector<P, ConversationBaseModel | undefined>,
  getContact: AppParametricSelector<P, ContactModel | undefined>,
) {
  return createSelector([getConversation, getContact, getCards], (conversation, contact, cards) => {
    if (!conversation || !contact) {
      return undefined;
    }

    return findCardForContactInNamespace(contact, cards, conversation.namespace);
  });
}

/** Find the channel, if any, for the conversation's contact. */
export function makeGetChannel<P>(getContact: AppParametricSelector<P, ContactModel | undefined>) {
  return createSelector([getContact, getChannels], (contact, channels) => {
    const channelId = contact?.channelId;
    if (!channelId) {
      return undefined;
    }

    return channels[channelId];
  });
}

export function makeGetVisualRecipient<P>(
  getConversation: AppParametricSelector<P, ConversationBaseModel | undefined>,
  getTeammateId: AppParametricSelector<P, number>,
  callSource?: string, // TODO(Mitchell): Remove this once the gc issue has been resolved
) {
  const getRecipient = makeGetRecipient(getConversation, getTeammateId);
  const getContact = makeGetContact(getRecipient);
  const getFallbackContact = makeGetFallbackContact(getContact);
  const getCard = makeGetCard(getConversation, getContact);
  const getChannel = makeGetChannel(getContact);
  let shouldReportError = true;

  return wrapInShallowSelector(
    createSelector(
      [getConversation, getRecipient, getContact, getFallbackContact, getCard, getChannel],
      (conversation, recipient, contact, fallbackContact, card, channel) => {
        if (!conversation) {
          return undefined;
        }

        if (!recipient) {
          return undefined;
        }

        if (!contact) {
          // Only report the error once per selector
          if (shouldReportError) {
            shouldReportError = false;
            reportToBugsnag(
              new ErrorWithData(`A contact could not be found for the recipient, could be a GC issue.`, {
                conversationId: conversation.id,
                source: callSource ?? 'unknown',
                cardId: card?.id,
                contactUid: recipient.contactUid,
              }),
            );
          }
          return undefined;
        }

        // Build the Viewer Recipient.
        const result: VisualRecipientModel = {
          contact,
          namespace: conversation.namespace,
          fallbackContact,
          card,
          channel,
          role: recipient.role,
          name: recipient.name,
          isSpammer: recipient.isSpammer,
        };
        return result;
      },
    ),
  );
}

/*
 * Multiple visual recipients.
 */

/** Find the contacts for the specified message. */
function makeGetMessageContacts<P>(getMessage: AppParametricSelector<P, MessageModel | undefined>) {
  return createSelector([getMessage, getContacts], (message, contacts) => {
    if (!message) {
      return [];
    }

    return message.recipients.map((recipient) => {
      const {contactUid} = recipient;
      const contact = contacts[contactUid];
      if (contact) {
        return contact;
      }

      // Tracking how often this happens now that the EML messages are loaded in the store.
      reportToBugsnag(
        new ErrorWithData('Contact for message not found.', {
          messageUid: message.uid,
          recipientHandle: recipient.handle,
          recipientContactUid: recipient.contactUid,
        }),
      );
      const handle = recipient.handle || parseContactUid(recipient.contactUid)[1];
      const source = getContactSourceFromMessageType(message.type);
      return buildContactForHandle(source, handle);
    });
  });
}

/** Find the fallback contacts for the specified contacts. */
function makeGetMessageFallbackContacts<P>(
  getMessageContacts: AppParametricSelector<P, ReadonlyArray<ContactModel>>,
) {
  return createSelector([getMessageContacts, getContacts], (messageContacts, contacts) =>
    messageContacts.map((c) => (c.fallbackContactUid && contacts[c.fallbackContactUid]) || undefined),
  );
}

/** Find the cards for the contacts of the current message. */
function makeGetMessageCards<P>(
  getNamespace: AppParametricSelector<P, string>,
  getMessageContacts: AppParametricSelector<P, ReadonlyArray<ContactModel>>,
) {
  return createSelector([getNamespace, getMessageContacts, getCards], (namespace, contacts, cards) =>
    contacts.map((contact) => findCardForContactInNamespace(contact, cards, namespace)),
  );
}

/** Find the channels for the contacts of the current message. */
function makeGetMessageChannels<P>(
  getMessageContacts: AppParametricSelector<P, ReadonlyArray<ContactModel>>,
) {
  return createSelector([getMessageContacts, getChannels], (contacts, channels) =>
    contacts.map((contact) => {
      const channelId = contact?.channelId;
      if (!channelId) {
        return undefined;
      }

      return channels[channelId];
    }),
  );
}

/** Build a flat list of visual recipients for the current message activity. */
export function makeGetVisualRecipients<P>(
  getNamespace: AppParametricSelector<P, string>,
  getMessage: AppParametricSelector<P, MessageModel | undefined>,
) {
  const getMessageContacts = makeGetMessageContacts(getMessage);
  const getMessageFallbackContacts = makeGetMessageFallbackContacts(getMessageContacts);
  const getMessageCards = makeGetMessageCards(getNamespace, getMessageContacts);
  const getMessageChannels = makeGetMessageChannels(getMessageContacts);

  const getMessageContactsShallow = wrapInShallowSelector(getMessageContacts);
  const getMessageFallbackContactsShallow = wrapInShallowSelector(getMessageFallbackContacts);
  const getMessageCardsShallow = wrapInShallowSelector(getMessageCards);
  const getMessageChannelsShallow = wrapInShallowSelector(getMessageChannels);

  return createSelector(
    [
      getNamespace,
      getMessage,
      getMessageContactsShallow,
      getMessageFallbackContactsShallow,
      getMessageCardsShallow,
      getMessageChannelsShallow,
    ],
    (namespace, message, contacts, fallbackContacts, cards, channels) => {
      if (!message) {
        return [];
      }

      return chain(message.recipients)
        .map((recipient, index) => {
          // Find the corresponding resources.
          const contact = contacts[index];
          const fallbackContact = fallbackContacts[index];
          const card = cards[index];
          const channel = channels[index];

          // Build the Viewer Recipient.
          const result: VisualRecipientModel = {
            contact,
            namespace,
            fallbackContact,
            card,
            channel,
            role: recipient.role,
            name: recipient.name,
          };
          return result;
        })
        .value();
    },
  );
}

export function findMessageRecipients(
  state: AppState,
  namespace: string,
  message: MessageModel,
): VisualRecipientModel[] {
  return makeGetVisualRecipients(
    () => namespace,
    () => message,
  )(state, undefined);
}

/** Build a map of visual recipients by role for the current message activity. */
export function makeGetVisualRecipientMap<P>(
  getNamespace: AppParametricSelector<P, string>,
  getMessage: AppParametricSelector<P, MessageModel | undefined>,
) {
  const getVisualRecipients = makeGetVisualRecipients(getNamespace, getMessage);
  return createSelector(
    getVisualRecipients,
    (recipients) => recipients && (groupBy(recipients, (r) => r.role) as VisualRecipientMap),
  );
}

export function makeGetRecipientForInterpolation<P>(
  getVisualRecipientMap: AppParametricSelector<P, VisualRecipientMap>,
): AppParametricSelector<P, VisualRecipientModel | undefined> {
  return createSelector(getVisualRecipientMap, (recipients: VisualRecipientMap) => {
    const recipientModels: VisualRecipientModel[] = chain(recipients).values().flatten().compact().value();

    return findRecipientForVariableInterpolation(recipientModels);
  });
}

export function makeGetRecipientContactsFromCard<P>(
  getRecipientForInterpolation: AppParametricSelector<P, VisualRecipientModel | undefined>,
  getContactsOverride?: AppSelector<ResourceMap<ContactModel>>, // For ease of testing.
) {
  return createSelector(
    [getRecipientForInterpolation, getContactsOverride || getContacts],
    (recipient, contacts) => {
      if (!recipient?.card) {
        return [];
      }

      return chain(recipient.card.contactUids)
        .map((contactUid) => contacts[contactUid])
        .compact()
        .value();
    },
  );
}

export function selectCardForInterpolation(
  recipientCards: ResourceMap<CardModel>,
  namespace: string,
  hasCompanyCards: boolean,
) {
  if (hasCompanyCards) {
    return selectCardViaPriorityOrder(_(recipientCards).values().compact().value());
  }
  return recipientCards[namespace];
}

export function makeGetRecipientCardForInterpolation<P>(
  getRecipientForInterpolation: AppParametricSelector<P, VisualRecipientModel | undefined>,
  getDraftNamespace: AppParametricSelector<P, string>,
  _getHasCompanyCardsEnabled: AppSelector<boolean>,
  getCardsOverride?: AppSelector<ResourceMap<CardModel>>,
): AppParametricSelector<P, CardModel | undefined> {
  return createSelector(
    [
      getRecipientForInterpolation,
      getCardsOverride || getCards,
      getDraftNamespace,
      _getHasCompanyCardsEnabled,
    ],
    (recipient, cards, draftNamespace, hasCompanyCardsEnabled) => {
      if (!recipient) {
        return undefined;
      }

      const recipientCards = mapValues(recipient.contact.cardIds, (cardId: number | undefined) =>
        cardId ? cards[cardId] : undefined,
      );

      // If we have company cards enabled, we will selected based on priority. If we do not have company
      // cards enabled, we will select based on namespace
      return selectCardForInterpolation(recipientCards, draftNamespace, hasCompanyCardsEnabled);
    },
  );
}
